import { useState } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { centsAusEingabe } from '../../../lib/geld'
import { Select, Symbol, TextField } from '../../../ui'
import { useAutomationEditor } from '../editor-context'
import type { ActionNodeData, ActionType } from '../types'

export function ActionNode({ id, data }: NodeProps) {
  const nodeData = data as unknown as ActionNodeData
  const type = nodeData.type ?? 'notification.send'
  const amountCents = nodeData.amountCents ?? 50
  const recipients = nodeData.recipients ?? 'guardians'

  const { edges, connectingNodeId, connectNodes, removeNode } = useAutomationEditor()
  const isCandidateTarget = Boolean(connectingNodeId && connectingNodeId !== id)
  const incomingEdges = edges.filter((e) => e.target === id)

  const [centsText, setCentsText] = useState(() => (amountCents / 100).toFixed(2).replace('.', ','))

  const notifyChange = () => {
    window.dispatchEvent(new CustomEvent('node-data-change', { detail: { id } }))
  }

  const updateType = (newType: string) => {
    nodeData.type = newType as ActionType
    if (newType === 'allowance.bonus') {
      nodeData.amountCents = 50
      nodeData.recipient = 'actor'
      setCentsText('0,50')
    } else if (newType === 'notification.send') {
      nodeData.recipients = 'guardians'
    }
    notifyChange()
  }

  const handleCentsChange = (val: string) => {
    setCentsText(val)
    const cents = centsAusEingabe(val)
    if (cents !== null && cents >= 0) {
      nodeData.amountCents = cents
      notifyChange()
    }
  }

  const updateRecipients = (r: string) => {
    nodeData.recipients = r as 'guardians' | 'all'
    notifyChange()
  }

  return (
    <div
      className={`editor-node editor-node--action ${
        isCandidateTarget ? 'editor-node--connecting-target' : ''
      }`}
      data-id={id}
    >
      <Handle
        id="action-in"
        type="target"
        position={Position.Left}
        isConnectable={true}
        title="Eingang: Verbindung hierher ziehen oder anklicken"
        className="editor-handle editor-handle--target"
      />

      {isCandidateTarget && connectingNodeId && (
        <button
          type="button"
          className="editor-node__anheften-btn"
          onClick={() => connectNodes(connectingNodeId, id)}
          title="Hier anheften"
        >
          <Symbol name="automation" width={16} height={16} />
          Hier verbinden
        </button>
      )}

      <div className="editor-node__kopf">
        <span className="editor-node__symbol">
          <Symbol name="plus" width={18} height={18} />
        </span>
        <span className="editor-node__kategorie">DANN (Aktion)</span>
        <button
          type="button"
          className="nodrag nopan editor-node__delete"
          onClick={(event) => {
            event.stopPropagation()
            removeNode(id)
          }}
          title="Aktion entfernen"
          aria-label="Aktion entfernen"
        >
          <Symbol name="trash" width={17} height={17} />
        </button>
      </div>

      <div className="editor-node__inhalt">
        <Select id={`act-type-${id}`} label="Aktionstyp" wert={type} onWert={updateType}>
          <option value="allowance.bonus">Taschengeld-Bonus gutschreiben</option>
          <option value="shopping.add_ingredients">Zutaten auf Einkaufsliste</option>
          <option value="notification.send">Benachrichtigung senden</option>
        </Select>

        {type === 'allowance.bonus' ? (
          <TextField
            id={`act-cents-${id}`}
            label="Bonus-Betrag (€)"
            inputMode="decimal"
            wert={centsText}
            onWert={handleCentsChange}
          />
        ) : type === 'notification.send' ? (
          <Select
            id={`act-recip-${id}`}
            label="Empfänger"
            wert={recipients}
            onWert={updateRecipients}
          >
            <option value="guardians">Erziehungsberechtigte</option>
            <option value="all">Alle Haushaltsmitglieder</option>
          </Select>
        ) : (
          <p className="editor-node__info">
            Übernimmt Zutaten auf die Standard-Einkaufsliste des Haushalts.
          </p>
        )}

        <div className="editor-node__verbindungen">
          <span className="editor-node__verbindungen-titel">
            {incomingEdges.length > 0
              ? `✓ Verbunden (${incomingEdges.length} Eingang)`
              : 'Noch nicht verknüpft'}
          </span>
        </div>
      </div>
    </div>
  )
}
